import type { ReactNode } from 'react';

/**
 * WorkingCredentials
 *
 * Editorial guide to the operational pillars of running a luxury travel
 * agency — Seller of Travel licensing, IATA + ARC, consortium membership,
 * preferred-partner programs, third-party endorsement, and proprietary
 * booking technology. Each row's left column explains the universal pillar;
 * the right column shows how LuxRally provides it.
 *
 * Visual format mirrors BeforeYouSell directly above — same hairline-bracketed
 * 2-col grid (kicker + Didot heading + italic standfirst on the left, prose
 * body on the right, vertical divider between columns at md+). Surface is
 * ivory #F5F5F5 — the cool light counterpart to the dark-teal cluster above.
 *
 * Top opener is a single centered chapter title above the 2-col rows.
 */

export function WorkingCredentials() {
    return (
        <section id="operating" className="scroll-mt-20 bg-[#F5F5F5]">
            <div className="mx-auto max-w-6xl px-6 pb-16 sm:pb-20 lg:pb-24">
                <div className="border-t border-b border-[#0e2727]/15">
                    {/* Chapter opener — centered, neutral framing of the operational pillars */}
                    <div className="border-b border-[#0e2727]/15 py-14 text-center sm:py-16 lg:py-20">
                        <p className="lux-caps lux-caps-sm text-[#0e2727]/55">
                            There&rsquo;s more than state requirements to consider
                        </p>
                        <h2
                            className="font-didot mx-auto mt-4 max-w-3xl text-[#0b2222]"
                            style={{ fontSize: 'clamp(1.85rem, 2.8vw, 2.5rem)', lineHeight: 1.1 }}
                        >
                            The Pillars of Selling and Earning
                        </h2>
                        <p
                            className="font-didot mx-auto mt-6 max-w-xl text-[#0e2727]/80 italic"
                            style={{
                                fontSize: 'clamp(1rem, 1.3vw, 1.2rem)',
                                lineHeight: 1.5,
                                letterSpacing: '0.005em',
                            }}
                        >
                            Beyond a state license, the pillars of a working luxury agency &mdash;
                            credentialing, consortium membership, supplier programs, third-party
                            legitimacy, and the rates and technology to deliver.
                        </p>
                    </div>

                    {/* Row — Seller of Travel: pillar (left) + LuxRally coverage (right) */}
                    <Row
                        kicker="Seller of Travel"
                        heading="Seller of Travel licensing."
                        standfirst="Several U.S. states require a Seller of Travel registration to sell travel commercially. The license belongs to whoever collects client funds — and carries real legal weight."
                    >
                        <div className="space-y-5 text-[#0e2727]/85 [&_p]:leading-[1.75] [&_strong]:font-semibold [&_strong]:text-[#0b2222]">
                            <p>
                                At LuxRally, <strong>we hold every Seller of Travel license required in
                                the United States</strong>. Our agents don&rsquo;t accept client payments
                                into their own accounts &mdash; every transaction runs through us
                                &mdash; so the licensing burden you&rsquo;d carry as an independent agent
                                stays with us, not you.
                            </p>
                            <p>
                                One small piece of state paperwork does stay with you. Because LuxRally
                                is registered as a Florida Seller of Travel, every LuxRally agent files
                                an annual{' '}
                                <a
                                    href="https://www.dochub.com/fillable-form/55353-seller-of-travel-affidavit-form-750"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline underline-offset-2 hover:text-[#0b2222]"
                                >
                                    Independent Agent Affidavit / Statement of Exemption
                                </a>{' '}
                                with the State of Florida and pays a <strong>$51.50 annual fee</strong>.
                                The filing applies no matter which state you live in, and{' '}
                                <strong>we walk you through the form and deadlines step by step in
                                orientation</strong> &mdash; it&rsquo;s a quick once-a-year task, not an
                                ongoing burden.
                            </p>
                            <p>
                                Accepting direct payments from clients changes the picture. If you
                                charge fees and collect them yourself, you may need your own SOT
                                registration plus additional business and tax compliance.{' '}
                                <strong>We don&rsquo;t recommend that path &mdash; at LuxRally, our
                                agents are banned from charging fees, but earn more on average through
                                our commissions and tools.</strong>
                            </p>
                        </div>
                    </Row>

                    {/* Row — IATA & ARC: pillar (left) + LuxRally coverage (right) + cognac */}
                    <Row
                        kicker="Industry identifiers"
                        heading="IATA and ARC."
                        standfirst="An IATA number is the universal industry identifier suppliers use to verify an agency is legitimate and decide whether to send commissions, net rates, and direct booking access. ARC complements it for airline ticketing."
                    >
                        <p className="leading-[1.75] text-[#0e2727]/85 [&_strong]:font-semibold [&_strong]:text-[#0b2222]">
                            <strong>LuxRally holds both.</strong> Every booking flows through our
                            IATA, so suppliers see one trusted agency identifier &mdash; and
                            commissions, net rates, and direct booking access come back to you.
                        </p>
                        <CognacPullQuote label="It tells suppliers, in one number,">
                            &ldquo;This is a legitimate agency. You can pay them.&rdquo;
                        </CognacPullQuote>
                    </Row>

                    {/* Row — Consortium: pillar (left) + LuxRally membership (right) */}
                    <Row
                        kicker="Consortium membership"
                        heading="Consortium membership."
                        standfirst="Consortia are buying groups — collectives of agencies that pool volume to negotiate better supplier rates, dedicated reps, and back-office systems than any single agency could secure alone."
                    >
                        <p className="leading-[1.75] text-[#0e2727]/85 [&_strong]:font-semibold [&_strong]:text-[#0b2222]">
                            LuxRally is a member of <strong>Travel Leaders Network</strong> &mdash;
                            one of the largest consortia in the industry &mdash; giving our agents
                            access to negotiated rates, supplier reps, and back-office
                            infrastructure.
                        </p>
                    </Row>

                    {/* Row — Preferred-partner programs: pillar (left) + LuxRally programs (right) + logos */}
                    <Row
                        kicker="Preferred partnerships"
                        heading="Preferred-partner programs."
                        standfirst="Most luxury suppliers run preferred-partner programs — better rates, signature amenities (room upgrades, daily breakfast, resort credits), and dedicated representation when something goes sideways."
                    >
                        <p className="leading-[1.75] text-[#0e2727]/85 [&_strong]:font-semibold [&_strong]:text-[#0b2222]">
                            LuxRally is in <strong>more than 18 of them</strong>, including Four
                            Seasons Preferred Partners, Hyatt Privé, Belmond Bellini Club, Rosewood
                            Elite, Peninsula PenClub, and Mandarin Oriental Fan Club. Every LuxRally
                            booking goes out under a program when one applies.
                        </p>
                        <a
                            href="/benefits"
                            className="lux-caps lux-caps-sm mt-6 inline-flex items-center gap-2 text-[#0b2222]/70 transition-colors hover:text-[#0b2222]"
                        >
                            See the full program list{' '}
                            <span aria-hidden="true">&rarr;</span>
                        </a>
                    </Row>

                    {/* Row — Third-party legitimacy: pillar (left) + Forbes endorsement (right) */}
                    <Row
                        kicker="Third-party legitimacy"
                        heading="Independent endorsement."
                        standfirst="Self-promotion can be designed. Independent third-party validation can’t — and luxury clients increasingly look for it when choosing who to book through."
                    >
                        <p className="leading-[1.75] text-[#0e2727]/85 [&_strong]:font-semibold [&_strong]:text-[#0b2222]">
                            LuxRally is one of a small group of agencies endorsed by{' '}
                            <strong>Forbes Travel Guide</strong> &mdash; the kind of trust signal
                            that doesn&rsquo;t come from self-promotion.
                        </p>
                        <img
                            src="/EndorsedByLogo-FTG-REV.png"
                            alt="Endorsed by Forbes Travel Guide"
                            loading="lazy"
                            className="mt-8 h-14 w-auto opacity-70 sm:h-16"
                            style={{ filter: 'invert(1)' }}
                        />
                    </Row>

                    {/* Row — Rates & technology (FastBook): pillar (left) + FastBook (right) + chapter-closing cognac */}
                    <Row
                        kicker="Rates and technology"
                        heading="Competitive rates, convenient tech."
                        standfirst="Rates come from many places — consortium pools, supplier programs, and the direct-contract relationships an agency builds with hotels and wholesalers over years. The technology to pull all of them together for a client at speed, and handle the booking, invoicing, and tracking that follow, is what turns access into a closed sale."
                        isLast
                    >
                        <p className="leading-[1.75] text-[#0e2727]/85 [&_strong]:font-semibold [&_strong]:text-[#0b2222]">
                            LuxRally built <strong>FastBook</strong> in-house &mdash; an AI-powered
                            booking platform that runs one search across <strong>GDS</strong>,{' '}
                            <strong>20+ wholesalers</strong>, and our{' '}
                            <strong>180,000+ direct hotel contracts</strong> (1.45M total
                            inventory), assembled over years of direct-supplier work.{' '}
                            <strong>As far as we know, no other host agency offers its agents more
                            rate coverage.</strong> Hotels, air, insurance, cars, and transfers in
                            one platform; cruise coming Q2 2026. Book, invoice, collect payment,
                            and track commissions from a single system &mdash; on desktop, tablet,
                            or phone.
                        </p>
                        <CognacPullQuote
                            attribution={
                                <>
                                    Chris Cardena{' '}
                                    <span className="text-[#0b2222]/30">&middot;</span> VP of
                                    Business Development
                                </>
                            }
                        >
                            &ldquo;LuxRally delivers the speed of an OTA, the margins of a tour
                            operator, and the control of an independent advisor &mdash; all without
                            the chaos of juggling multiple systems.&rdquo;
                        </CognacPullQuote>
                    </Row>
                </div>
            </div>
        </section>
    );
}

function Row({
    kicker,
    heading,
    headingLevel = 'h3',
    standfirst,
    children,
    isLast = false,
}: {
    kicker: string;
    heading: string;
    headingLevel?: 'h2' | 'h3';
    standfirst?: ReactNode;
    children: ReactNode;
    isLast?: boolean;
}) {
    const HeadingTag = headingLevel;
    const headingFontSize =
        headingLevel === 'h2'
            ? 'clamp(1.85rem, 2.8vw, 2.5rem)'
            : 'clamp(1.45rem, 2vw, 1.85rem)';

    return (
        <div
            className={`grid gap-10 py-10 sm:py-12 lg:py-14 md:grid-cols-2 md:gap-0 ${
                isLast ? '' : 'border-b border-[#0e2727]/15'
            }`}
        >
            <div className="md:pr-10 lg:pr-14">
                <p className="lux-caps lux-caps-sm text-[#0e2727]/55">{kicker}</p>
                <HeadingTag
                    className="font-didot mt-4 text-[#0b2222]"
                    style={{ fontSize: headingFontSize, lineHeight: 1.1 }}
                >
                    {heading}
                </HeadingTag>
                {standfirst && (
                    <p
                        className="font-didot mt-5 text-[#0e2727]/80 italic"
                        style={{
                            fontSize: 'clamp(1rem, 1.3vw, 1.2rem)',
                            lineHeight: 1.5,
                            letterSpacing: '0.005em',
                        }}
                    >
                        {standfirst}
                    </p>
                )}
            </div>
            <div className="md:border-l md:border-[#0e2727]/15 md:pl-10 lg:pl-14">{children}</div>
        </div>
    );
}

function CognacPullQuote({
    label,
    children,
    attribution,
}: {
    label?: string;
    children: ReactNode;
    attribution?: ReactNode;
}) {
    return (
        <div className="mt-8 border-l-2 pl-5" style={{ borderColor: '#7C4D2E' }}>
            {label && (
                <p className="text-[#0e2727]/75 sm:text-base" style={{ letterSpacing: '0.01em' }}>
                    <span className="text-[#7C4D2E]">&rarr;</span> {label}
                </p>
            )}
            <p
                className={`font-didot text-[#0b2222] italic ${label ? 'mt-2' : ''}`}
                style={{ fontSize: 'clamp(1.15rem, 1.55vw, 1.4rem)', lineHeight: 1.4 }}
            >
                {children}
            </p>
            {attribution && (
                <p className="lux-caps lux-caps-sm mt-3 text-[#0b2222]/60">{attribution}</p>
            )}
        </div>
    );
}
